import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import BasePagination from "@/components_v2/Pagination/BasePagination";
import BaseSkeleton from "@/components_v2/Skeleton/BaseSkeleton";
import BaseSkeletonGroup from "@/components_v2/Skeleton/BaseSkeletonGroup";
import { SKELETON_VARIANTS } from "@/components_v2/Skeleton/constants";
import {
  useGetKnowledgeCheckAttemptsQuery,
  useGetParticipantsQuery,
} from "@/redux/service";
import type {
  AdminKnowledgeCheckAttemptListItem,
  Participant,
} from "@/redux/certifications/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import AttemptCard from "@/features/certifications/knowledge-check-flags/components/AttemptCard";
import ReviewModal from "@/features/certifications/knowledge-check-flags/components/ReviewModal";
import {
  ATTEMPT_SKELETON_CARDS,
  ATTEMPTS_PAGE_SIZE,
} from "@/features/certifications/knowledge-check-flags/constants";
import {
  KnowledgeCheckFlagsTabProps,
  ReviewDraft,
} from "@/features/certifications/knowledge-check-flags/types";
import { ATTEMPT_STATES } from "@/domains/certifications/constants";

import "./styles.css";

const KnowledgeCheckFlagsTab = ({
  certification,
}: KnowledgeCheckFlagsTabProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [reviewing, setReviewing] =
    useState<AdminKnowledgeCheckAttemptListItem | null>(null);
  const [openAttemptId, setOpenAttemptId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, ReviewDraft>>({});

  const { data, error, isLoading, isFetching, isError } =
    useGetKnowledgeCheckAttemptsQuery({
      certification: certification.id,
      state: ATTEMPT_STATES.PENDING_HUMAN_REVIEW,
      page,
      pageSize: ATTEMPTS_PAGE_SIZE,
    });
  const participants = useGetParticipantsQuery({
    certificationSlug: certification.slug,
    pagination: "off",
  });

  const participantsByEnrollment = useMemo(() => {
    const rows = participants.data
      ? isPaginatedResponse(participants.data)
        ? participants.data.results
        : participants.data
      : [];
    return new Map<number, Participant>(rows.map((row) => [row.id, row]));
  }, [participants.data]);

  const attempts = data
    ? isPaginatedResponse(data)
      ? data.results
      : data
    : [];
  const total =
    data && isPaginatedResponse(data) ? data.count : attempts.length;

  const isPastLastPage =
    page > 1 &&
    error !== undefined &&
    "status" in error &&
    error.status === 404;
  useEffect(() => {
    if (isPastLastPage) {
      setPage(1);
    }
  }, [isPastLastPage]);

  const handleReview = (attempt: AdminKnowledgeCheckAttemptListItem) => {
    setReviewing(attempt);
    setOpenAttemptId(attempt.id);
  };

  const handleDraftChange = (attemptId: number, draft: ReviewDraft) =>
    setDrafts((current) => ({ ...current, [attemptId]: draft }));

  const handleDecided = (attemptId: number) => {
    setDrafts((current) => {
      const next = { ...current };
      delete next[attemptId];
      return next;
    });
    setOpenAttemptId((current) => (current === attemptId ? null : current));
    if (attempts.length === 1 && page > 1) {
      setPage(page - 1);
    }
  };

  let content;
  if (isLoading) {
    content = (
      <BaseSkeletonGroup
        label={t("labels.loading")}
        className="flex flex-col gap-300"
      >
        {Array.from({ length: ATTEMPT_SKELETON_CARDS }, (_, index) => (
          <div
            key={index}
            className="kc-card kc-card-body flex flex-col gap-300"
          >
            <div className="flex items-center gap-200">
              <BaseSkeleton
                variant={SKELETON_VARIANTS.CIRCLE}
                width="var(--spacing-600)"
                height="var(--spacing-600)"
              />

              <BaseSkeleton width="10rem" />
            </div>

            <BaseSkeleton variant={SKELETON_VARIANTS.TEXT} lines={2} />
          </div>
        ))}
      </BaseSkeletonGroup>
    );
  } else if (isError) {
    content = (
      <p className="kc-message flex justify-center body-m-regular" role="alert">
        {t("certification.admin.knowledgeCheckFlags.error")}
      </p>
    );
  } else if (attempts.length === 0) {
    content = (
      <p className="kc-message flex justify-center body-m-regular">
        {t("certification.admin.knowledgeCheckFlags.empty")}
      </p>
    );
  } else {
    content = (
      <div
        className={`flex flex-col gap-300 ${isFetching ? "kc-refreshing" : ""}`}
        aria-busy={isFetching}
      >
        {attempts.map((attempt) => (
          <AttemptCard
            key={attempt.id}
            attempt={attempt}
            participant={participantsByEnrollment.get(attempt.enrollment)}
            certification={certification}
            onReview={handleReview}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-400">
      {content}

      {total > ATTEMPTS_PAGE_SIZE && (
        <div className="flex justify-end">
          <BasePagination
            current={page}
            pageSize={ATTEMPTS_PAGE_SIZE}
            total={total}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      )}

      <ReviewModal
        open={reviewing !== null && reviewing.id === openAttemptId}
        attempt={reviewing}
        participant={
          reviewing
            ? participantsByEnrollment.get(reviewing.enrollment)
            : undefined
        }
        certification={certification}
        draft={reviewing ? (drafts[reviewing.id] ?? null) : null}
        onDraftChange={handleDraftChange}
        onClose={() => setOpenAttemptId(null)}
        onDecided={handleDecided}
      />
    </div>
  );
};

export default KnowledgeCheckFlagsTab;
